import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchPublicReport, sendReportInterest, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Gauge,
  MapPin,
  Search,
  Star,
  CheckCircle2,
  Loader2,
  MessageCircle,
  Send,
  Rocket,
  Scale,
  Download,
  MessagesSquare,
} from "lucide-react";
import { toast } from "sonner";
import useNoindex from "@/lib/useNoindex";
import { T } from "@/components/report/i18n";
import OverviewTab from "@/components/report/OverviewTab";
import VisibilityTab from "@/components/report/VisibilityTab";
import ReputationTab from "@/components/report/ReputationTab";
import LegalTab from "@/components/report/LegalTab";
import PlanTab from "@/components/report/PlanTab";
import ConsultingTab from "@/components/report/ConsultingTab";
import { consultingState } from "@/components/report/consultingModel";
import { ReturnBanner, RETURN_POLL_DELAYS_MS, parseCheckoutReturn, stripCheckoutParam } from "@/components/report/checkoutReturn";
import { BrandLogo } from "@/components/BrandLogo";
import SummaryReport from "@/components/report/SummaryReport";
import { SENDER, whatsappLink } from "@/components/report/salesConfig";
import {
  TAB_KEYS,
  initials, scoreTone,
} from "@/components/report/reportUtils";

// Real client references, once there are any — renders nothing until then.
// { name: "Musterfirma GmbH", logoUrl: "/logos/musterfirma.png" }
const CLIENT_LOGOS = [];

const TAB_ICONS = { overview: Gauge, visibility: Search, reputation: Star, legal: Scale, plan: Rocket, consulting: MessagesSquare };

export default function ReportPage() {
  useNoindex();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [activeTab, setActiveTab] = useState("overview");
  const [photoFailed, setPhotoFailed] = useState(false);

  // ?checkout=success|cancel from Stripe: remembered once, then removed from the URL.
  const [returnState, setReturnState] = useState(() =>
    typeof window === "undefined" ? null : parseCheckoutReturn(window.location.search)
  );
  const cleaned = useRef(false);

  useEffect(() => {
    fetchPublicReport(token)
      .then(setData)
      .catch(() => setError(true));
  }, [token]);

  useEffect(() => {
    if (cleaned.current || typeof window === "undefined" || !window.history || !parseCheckoutReturn(window.location.search)) return;
    cleaned.current = true;
    window.history.replaceState(null, "", stripCheckoutParam(window.location.href));
  }, []);

  // The webhook can lag: refetch once after 3s and once after 8s, then stop.
  useEffect(() => {
    if (returnState !== "success") return undefined;
    const timers = RETURN_POLL_DELAYS_MS.map((ms) =>
      setTimeout(() => {
        fetchPublicReport(token).then(setData).catch(() => {});
      }, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [returnState, token]);

  const retryCheckout = useCallback(() => {
    setReturnState(null);
    setActiveTab("consulting");
    if (typeof document !== "undefined") document.getElementById("angebot")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const setField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // PDF export = the browser's own "Print to PDF" (user decision 2026-09-14,
  // over a server-rendered PDF — zero new backend/dependency, works
  // everywhere). Two things it needs that the on-screen layout doesn't:
  // all 5 tabs' content in the DOM at once (the print CSS below reveals
  // them all, but React only mounts what's already there), and every
  // collapsed accordion section opened so nothing is silently missing from
  // the printout. Accordions here are uncontrolled (defaultValue only), so
  // rather than lifting state across three files, this walks the DOM for
  // whatever's still closed right before printing and clicks it open —
  // then clicks the same buttons again after the print dialog closes to
  // restore exactly the collapsed state the reader had on screen.
  const handleDownloadPdf = () => {
    const closedTriggers = Array.from(document.querySelectorAll('button[data-state="closed"]'));
    closedTriggers.forEach((btn) => btn.click());
    const restore = () => {
      closedTriggers.forEach((btn) => btn.click());
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    // One tick so React has re-rendered the now-expanded accordion content
    // before the print dialog captures the page.
    setTimeout(() => window.print(), 50);
  };

  const requestDetail = async (e) => {
    e.preventDefault();
    setRequesting(true);
    // Not the `lang`/`L` consts below — this closure can be called well
    // before or after that line runs across renders, so it derives the
    // language directly from `data` instead of relying on them.
    const l = data?.lang === "en" ? "en" : "de";
    try {
      await sendReportInterest(token, form);
      setRequested(true);
      toast.success(T[l].interestSent);
    } catch (e2) {
      toast.error(T[l].interestFailed);
    } finally {
      setRequesting(false);
    }
  };

  if (error) {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg items-center justify-center px-4 text-center">
        <div>
          <h1 className="font-heading text-xl font-bold">{T.de.reportNotFoundTitle}</h1>
          <p className="mt-2 text-base text-muted-foreground">
            {T.de.reportNotFoundBody}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-4 py-10">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  const lang = data.lang === "en" ? "en" : "de";
  const L = T[lang];
  const tone = scoreTone(data.digital_score);
  const isDetailed = !!data.is_detailed;
  const photoUrl = `${API}/report/${token}/photo`;
  // The consulting tab exists in the paid layout only, and only when the
  // backend sent the `consulting` block (absent on the free tier).
  const hasConsulting = isDetailed && consultingState(data) !== "absent";
  const tabKeys = hasConsulting ? [...TAB_KEYS, "consulting"] : TAB_KEYS;

  // Photo header: the business's own Google Maps cover photo when one
  // exists (see backend's pick_cover_photo_ref), initials avatar
  // otherwise — no reliable logo source exists for these prospects
  // (no self-serve upload, favicons are usually generic/low-quality),
  // so a real photo beats a stand-in logo. User decision 2026-09-07;
  // validated against a Claude Design mockup before this edit.
  const brandBar = (
    <div className="border-b bg-white">
      <div className="mx-auto flex h-16 max-w-3xl items-center px-4">
        <BrandLogo className="h-11" />
      </div>
    </div>
  );
  const header = (
      <div
        className="relative h-32 overflow-hidden sm:h-40"
        style={{ background: "linear-gradient(160deg, hsl(28 65% 88%), hsl(182 45% 82%))" }}
      >
        {!photoFailed && (
          <img
            src={photoUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            onError={() => setPhotoFailed(true)}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 mx-auto flex w-full max-w-3xl items-center gap-3 px-4 pb-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-base font-bold text-white shadow"
            style={{ background: "linear-gradient(135deg, hsl(217 100% 59%), hsl(182 100% 39%))" }}
          >
            {initials(data.name)}
          </div>
          <div className="min-w-0">
            <h1 title={data.name} className="line-clamp-2 break-words font-heading text-lg font-semibold leading-tight text-white sm:text-xl">{data.name}</h1>
            {(data.category || data.address) && (
              <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-white/85">
                <MapPin className="h-3 w-3 shrink-0" />
                {[data.category, data.address].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
        </div>
      </div>
  );

  // Free tier: one focused page (no tabs, no PDF button — the PDF export
  // belongs to the paid report). The paid layout below is untouched.
  if (!isDetailed) {
    return (
      <div className="min-h-screen bg-background">
        {brandBar}
        {header}
        <ReturnBanner state={returnState} data={data} lang={lang} onRetry={retryCheckout} />
        <SummaryReport data={data} lang={lang} token={token} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {brandBar}
      {header}
      <ReturnBanner state={returnState} data={data} lang={lang} onRetry={retryCheckout} />

      {/* Tab bar — replaces the ~20-card single-column stack with 5 topic
          tabs (Übersicht/Sichtbarkeit/Reputation/Recht & Struktur/
          Aktionsplan); no data or section content changed, only grouping. */}
      <div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur print:hidden">
        <div className="mx-auto flex max-w-3xl items-center gap-1.5 overflow-x-auto px-4 py-2">
          {tabKeys.map((key) => {
            const Icon = TAB_ICONS[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeTab === key
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {L.tabs[key]}
              </button>
            );
          })}
          <button
            type="button"
            onClick={handleDownloadPdf}
            className="ml-auto flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary"
          >
            <Download className="h-3.5 w-3.5" /> {L.downloadPdf}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-6 sm:py-8">
        {/* Every tab below stays mounted at all times (not just the active
            one) so `handleDownloadPdf`'s print CSS can reveal all 5 in one
            stacked printout — only `hidden`/`print:block` controls whether
            a given tab is visible on screen right now. */}
        {/* ===================== Übersicht ===================== */}
        <div className={activeTab === "overview" ? undefined : "hidden print:block"}>
          <h2 className="hidden font-heading text-xl font-bold print:block">{L.tabs.overview}</h2>
          <OverviewTab data={data} lang={lang} L={L} tone={tone} setActiveTab={setActiveTab} />
        </div>

        {/* ===================== Sichtbarkeit ===================== */}
        <div className={activeTab === "visibility" ? undefined : "hidden print:block"}>
          <h2 className="hidden font-heading text-xl font-bold print:mt-10 print:block">{L.tabs.visibility}</h2>
          <VisibilityTab data={data} lang={lang} L={L} tone={tone} />
        </div>

        {/* ===================== Reputation ===================== */}
        <div className={activeTab === "reputation" ? undefined : "hidden print:block"}>
          <h2 className="hidden font-heading text-xl font-bold print:mt-10 print:block">{L.tabs.reputation}</h2>
          <ReputationTab data={data} lang={lang} L={L} />
        </div>

        {/* ===================== Recht & Struktur ===================== */}
        <div className={activeTab === "legal" ? undefined : "hidden print:block"}>
          <h2 className="hidden font-heading text-xl font-bold print:mt-10 print:block">{L.tabs.legal}</h2>
          <LegalTab data={data} L={L} token={token} />
        </div>

        {/* ===================== Aktionsplan ===================== */}
        {/* Wide report: the paid 3-month action plan. Only ever present when
            an admin has approved it (server.py gates this — see PRD §5), so
            no separate "is this a draft" check is needed here. PlanTab
            itself renders the empty state when wideReport is falsy. */}
        <div className={activeTab === "plan" ? undefined : "hidden print:block"}>
          <h2 className="hidden font-heading text-xl font-bold print:mt-10 print:block">{L.tabs.plan}</h2>
          <PlanTab data={data} L={L} />
        </div>

        {/* ===================== Beratung (paid only, never printed) ===================== */}
        {hasConsulting && activeTab === "consulting" && (
          <div className="print:hidden">
            <ConsultingTab data={data} lang={lang} token={token} />
          </div>
        )}

        {/* CTA / request form. Copy branches on isDetailed: the summary
            tier sells the detailed report by naming, concretely, what it
            unlocks — the paid report's own CTA just stays a low-pressure
            contact option, since there's nothing further to unlock. Makes
            no sense on a printed page — a form can't be submitted on
            paper — so it's excluded from the printout. */}
        <div className="mt-8 rounded-2xl border border-primary/30 bg-accent/40 p-5 sm:p-6 print:hidden">
          {requested ? (
            <div className="flex flex-col items-center gap-1.5 py-2 text-center text-base">
              <CheckCircle2 className="h-6 w-6 text-primary" />
              <p className="font-medium">{L.thankYou}</p>
            </div>
          ) : (
            <>
              <h3 className="text-center font-heading text-base font-semibold">
                {L.ctaTitleDetailed}
              </h3>
              <p className="mx-auto mt-1 max-w-md text-center text-base text-muted-foreground">
                {L.ctaDesc}
              </p>
              <form onSubmit={requestDetail} className="mx-auto mt-4 max-w-sm space-y-2.5">
                <Input
                  placeholder={L.formName}
                  value={form.name}
                  onChange={setField("name")}
                  data-testid="report-request-name"
                />
                <Input
                  placeholder={L.formContact}
                  value={form.contact}
                  onChange={setField("contact")}
                  required
                  data-testid="report-request-contact"
                />
                <Textarea
                  placeholder={L.formMessage}
                  rows={2}
                  value={form.message}
                  onChange={setField("message")}
                  data-testid="report-request-message"
                />
                <Button type="submit" className="w-full" disabled={requesting} data-testid="report-request-submit">
                  {requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {L.sendMessage}
                </Button>
              </form>
            </>
          )}
        </div>

        {/* Client references — hidden until CLIENT_LOGOS has real entries */}
        {CLIENT_LOGOS.length > 0 && (
          <div className="mt-8 border-t pt-6 text-center">
            <p className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
              {L.trustedBy}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {CLIENT_LOGOS.map((c, i) =>
                c.logoUrl ? (
                  <img key={i} src={c.logoUrl} alt={c.name} className="h-8 w-auto opacity-80 grayscale" />
                ) : (
                  <span key={i} className="rounded-full border bg-card px-3 py-1 text-sm font-medium text-muted-foreground">
                    {c.name}
                  </span>
                )
              )}
            </div>
          </div>
        )}

        {SENDER.name && (
          <div className="mt-8 border-t pt-5 text-center text-sm text-muted-foreground">
            <p className="font-medium text-foreground">{SENDER.company ? `${SENDER.name} · ${SENDER.company}` : SENDER.name}</p>
            <div className="mt-1 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              {whatsappLink(SENDER.phone) && (
                <a href={whatsappLink(SENDER.phone)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              )}
              {SENDER.phone && <span>{SENDER.phone}</span>}
              {SENDER.email && <span>{SENDER.email}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
